import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { connect } from "react-redux";
import { differenceInDays } from "date-fns";
import { LuPartyPopper } from "react-icons/lu";
import { PiHandCoinsFill } from "react-icons/pi";
import { Col, Container, Row } from "react-bootstrap";
import { MdKeyboardArrowRight, MdOutlineBarChart } from "react-icons/md";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronUp,
  faChevronDown,
  faHeadSideVirus,
} from "@fortawesome/free-solid-svg-icons";

import {
  brandPerformanceConstants,
  emptyString,
  AVERAGE_RATING_RESPONSE_TYPE,
} from "../../constants";
import {
  useSpinner,
  Spinner,
  isGroupAdmin,
  isManagerAdmin,
  isSuperOrBrandAdmin,
  convertToDateToUnix,
  convertFromDateToUnix,
  datePreferenceUtility,
} from "../../../../shared";
import CustomDateFilter from "../../../../shared/components/dateFilter/customDateFilter";
import { brandPerformanceActions } from "../../redux/actions";

const {
  GET_AVERAGE_RATING_RESPONSES,
  STORES_AVERAGE_SENTIMENT_AND_TRANSACTION_RATING,
} = brandPerformanceConstants;

const Dashboard = (props) => {
  const {
    logedUser,
    getAverageRatingResponses,
    brandPerformanceReducer: {
      storesAvgSentimentAndTransactionRatingData,
      groupsAvgSentimentAndTransactionRatingData,
    },
  } = props;

  const { brandId = logedUser.brandId, groupId } = useParams();
  const [dayCounts, setDayCounts] = useState(emptyString);
  const [dateState, setDateState] = useState(datePreferenceUtility());
  const [surveyPerformanceData, setSurveyPerformanceData] = useState({});

  const createDateFrom = convertFromDateToUnix(dateState.startDate);
  const createDateTo = convertToDateToUnix(dateState.endDate);

  //Destructure performance data
  const {
    currentResponseCount,
    previousResponseCount,
    averageSentimentRatings,
    averageTransactionRatings,
    previousAverageSentimentRatings,
    previousAverageTransactionRatings,
  } = surveyPerformanceData;

  const requestBody = {
    length: 10,
    search: "",
    pageNumber: 0,
    IsCallFromDashBoard: true,
    orderBy: "name desc",
    isGuestSentimentFilter: true,
    minAverageGuestRatings: ["0"],
    maxAverageGuestRatings: ["5.0"],
    filter: `(SurveyCompletionDate > ${createDateFrom} and SurveyCompletionDate <= ${createDateTo})`,
  };

  // Show loading while a request is running.
  const spinner = useSpinner();

  //Function to calculate percentage on the basis of old and new value
  const calculatePercentageChange = (newValue, oldValue) => {
    if (oldValue === 0 && newValue === 0) {
      return "0.00% ";
    }
    if (oldValue === 0 && newValue !== 0) {
      return `100% `;
    }

    const numerator = Math.abs(newValue - oldValue);
    let percentage = (numerator / oldValue) * 100;
    percentage = Math.abs(
      parseFloat(isNaN(percentage) ? 0 : percentage),
    ).toFixed(2);

    return `${percentage}% `;
  };

  const getRatingChangeClassAndIcon = (currentValue, previousValue) => {
    if (currentValue > previousValue) {
      return {
        className: "text-success",
        icon: <FontAwesomeIcon icon={faChevronUp} className="me-1" />,
      };
    }
    if (currentValue === previousValue) {
      return {
        className: "text-secondary",
        icon: null,
      };
    }
    return {
      className: "text-danger",
      icon: <FontAwesomeIcon icon={faChevronDown} className="me-1" />,
    };
  };

  //Function to render trend icon on the basis of current and previous value
  const renderTrendIcon = (currentValue, previousValue) => {
    return (
      <div
        className={`d-flex align-items-center justify-content-center ${
          getRatingChangeClassAndIcon(currentValue, previousValue).className
        } `}
      >
        <div className="p-1 rounded me-2">
          {getRatingChangeClassAndIcon(currentValue, previousValue).icon}
          {calculatePercentageChange(currentValue, previousValue)}
          since last {dayCounts} days
        </div>
      </div>
    );
  };

  //Function to handle navigation to performance page
  const navigateToPerformancePage = () => {
    if (isSuperOrBrandAdmin()) {
      return `/stores/dashboard/${brandId}/brand-performance`;
    } else if (isGroupAdmin()) {
      return `/dashboard/${groupId}/group-performance`;
    } else {
      return `/dashboard/${groupId}/manager-performance`;
    }
  };

  const isSpinnerRunning = () =>
    spinner(STORES_AVERAGE_SENTIMENT_AND_TRANSACTION_RATING) ||
    spinner(GET_AVERAGE_RATING_RESPONSES);

  //Getting selected days to display
  useEffect(() => {
    const { startDate, endDate } = dateState;
    const days = differenceInDays(endDate, startDate);
    const daysCount = days + 1;
    setDayCounts(daysCount);

    const {
      GET_STORE_AVERAGE_RATING_RESPONSES,
      GET_GROUPS_AVERAGE_RATING_RESPONSES,
      GET_MANAGER_AVERAGE_RATING_RESPONSES,
    } = AVERAGE_RATING_RESPONSE_TYPE;
    //Getting survey data
    if (isSuperOrBrandAdmin()) {
      const payload = {
        id: brandId,
        createDateTo: createDateTo,
        createDateFrom: createDateFrom,
        type: GET_STORE_AVERAGE_RATING_RESPONSES,
        ...requestBody,
      };
      getAverageRatingResponses(payload);
    } else if (isGroupAdmin()) {
      const payload = {
        id: groupId,
        createDateTo: createDateTo,
        createDateFrom: createDateFrom,
        type: GET_GROUPS_AVERAGE_RATING_RESPONSES,
        ...requestBody,
      };
      getAverageRatingResponses(payload);
    } else if (isManagerAdmin()) {
      const payload = {
        id: groupId,
        createDateTo: createDateTo,
        createDateFrom: createDateFrom,
        type: GET_MANAGER_AVERAGE_RATING_RESPONSES,
        ...requestBody,
      };
      getAverageRatingResponses(payload);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateState]);

  useEffect(() => {
    if (
      isGroupAdmin() &&
      Object.keys(groupsAvgSentimentAndTransactionRatingData || {}).length
    ) {
      setSurveyPerformanceData(groupsAvgSentimentAndTransactionRatingData);
    } else if (
      Object.keys(storesAvgSentimentAndTransactionRatingData || {}).length
    ) {
      setSurveyPerformanceData(storesAvgSentimentAndTransactionRatingData);
    }

    //Empty state on component unmount
    return () => {
      setSurveyPerformanceData({});
    };
  }, [
    storesAvgSentimentAndTransactionRatingData,
    groupsAvgSentimentAndTransactionRatingData,
  ]);

  return (
    <>
      {isSpinnerRunning() ? (
        <Spinner />
      ) : (
        <div className="container-fluid pt-3">
          <div className="pb-2">
            <CustomDateFilter
              dateState={dateState}
              setDateState={setDateState}
              className="border border-1 bg-white rounded"
            />
          </div>
          <div
            className="notification-form shadow card px-3 py-2 mb-3 border"
            data-testid="survey-dashboard-card"
          >
            <div className="me-auto d-flex align-items-center">
              <div className="dashboard-icon rounded">
                <MdOutlineBarChart size={20} className="m-1" />
              </div>
              <h5 className="mb-0 px-2">Survey</h5>
            </div>
            <Container className="px-lg-5 px-md-4">
              <Row className="d-flex py-3 g-lg-1 g-3">
                <Col
                  lg={4}
                  className="d-flex flex-column justify-content-between gap-3"
                >
                  <div className="d-flex align-items-top justify-content-center ">
                    <LuPartyPopper className="me-1 mt-1" />
                    <div className="lh-sm ">
                      <p className="mb-0 fw-bold">Total Responses</p>
                      <small>Last {dayCounts} days</small>
                    </div>
                  </div>
                  <h3 className="bold-6 text-center mb-0 py-1 green-color">
                    {currentResponseCount}
                  </h3>
                  <div className="d-flex align-items-center justify-content-center">
                    {renderTrendIcon(
                      currentResponseCount,
                      previousResponseCount,
                    )}
                  </div>
                </Col>
                <hr className="d-lg-none" />
                <Col
                  lg={4}
                  className="d-flex flex-column justify-content-between align-items-center"
                >
                  <div className="d-flex align-items-top justify-content-center ">
                    <PiHandCoinsFill className="me-1 mt-1" />
                    <div className="lh-sm ">
                      <p className="mb-0 fw-bold">Average Transaction Rating</p>
                      <small>Last {dayCounts} days</small>
                    </div>
                  </div>
                  <h2 className="green-color fw-bold">
                    {averageTransactionRatings}
                  </h2>
                  <div>
                    {renderTrendIcon(
                      averageTransactionRatings,
                      previousAverageTransactionRatings,
                    )}
                  </div>
                </Col>
                <hr className="d-lg-none" />
                <Col
                  lg={4}
                  className="d-flex flex-column justify-content-between align-items-center "
                >
                  <div className="d-flex align-items-top justify-content-center ">
                    <FontAwesomeIcon
                      icon={faHeadSideVirus}
                      className="me-1 mt-1"
                    />
                    <div className="lh-sm ">
                      <p className="mb-0 fw-bold">Average Sentiment Rating</p>
                      <small>Last {dayCounts} days</small>
                    </div>
                  </div>
                  <h2 className="green-color fw-bold">
                    {averageSentimentRatings}
                  </h2>
                  <div>
                    {renderTrendIcon(
                      averageSentimentRatings,
                      previousAverageSentimentRatings,
                    )}
                  </div>
                </Col>
              </Row>
            </Container>
            <div className="text-end ">
              <Link
                to={navigateToPerformancePage()}
                className="text-decoration-none"
              >
                See more <MdKeyboardArrowRight size={20} />
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  logedUser: state.authentication.user,
  reduxBrandId: state.storesReducer.brandId,
  brandPerformanceReducer: state.brandPerformanceReducer,
});

const mapDispatchToProps = (dispatch) => ({
  getAverageRatingResponses: (payload) =>
    dispatch(brandPerformanceActions.getAverageRatingResponses(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
